// Shared-surname rules for matching office sheet labels to the signed-in user.
//
// The office only adds a distinguishing initial when it NEEDS one to tell two
// working pilots apart ("MERRILL, B" vs "MERRILL, P"; dispatch "Collins D/E").
// A TRAINEE who shares a surname with a full pilot is not on the office's
// radar, so a bare-surname label always means the full pilot — the trainee
// only ever owns a label with their explicit initial. (Ivan vs Tom Larson,
// 2026-07-04: Ivan saw Tom's dispatch and travel.)

// Does this same-surname group force the CURRENT user to require an explicit
// initial before claiming bare labels? True when the user is a trainee
// (trainee_phase set) and a same-surname NON-trainee exists.
export function traineeDefersToFullPilot(me, sameSurnamePilots) {
  if (!me?.trainee_phase) return false;
  return (sameSurnamePilots || []).some(p => p.id !== me.id && !p.trainee_phase);
}

// Dispatch-sheet labels can carry a "GPM" prefix ("GPM Sizemore J") and
// tonnage tokens ("Larson 50k", "Merrill B 110"). Strip them so the first
// remaining token is the surname — every surname comparison against a
// dispatch label must go through this (GPM pilots had empty My Schedules
// and invisible training-pilot days before it existed, 2026-07-07).
export function dispatchLabelCore(label) {
  return (label || "")
    .replace(/\bGPM\b/gi, "")
    .replace(/\b\d+K?\b/gi, "")
    .trim()
    .replace(/\s+/g, " ");
}

// Which of a set of dispatch-sheet labels belong to a GIVEN pilot. Mirrors the
// My Schedule myLabels logic so shared surnames resolve correctly: the token
// after the surname can be an INITIAL ("Merrill B") or just a home-port
// designator ("Merrill J" = Paul, Juneau — NOT initial "J"). A single-letter
// token only counts as an initial when a same-surname pilot actually has that
// initial (claimedInitials); a bare label goes to the untagged full pilot.
export function dispatchLabelsForPilot(allLabels, pilot, allPilots) {
  const pLast = (pilot?.last_name || "").toUpperCase().trim();
  if (!pLast) return new Set();
  const surnameLabels = [...new Set((allLabels || []).filter(
    (l) => dispatchLabelCore(l).toUpperCase().split(/\s+/).filter(Boolean)[0] === pLast))];
  if (!surnameLabels.length) return new Set();
  const shared = (allPilots || []).filter((p) => (p.last_name || "").toUpperCase().trim() === pLast);
  if (shared.length <= 1) return new Set(surnameLabels);

  const tokenOf = (lab) => dispatchLabelCore(lab).toUpperCase().split(/\s+/).slice(1).find((t) => /^[A-Z]$/.test(t)) || "";
  const claimed = new Set(surnameLabels.map(tokenOf).filter(
    (tok) => shared.some((p) => (p.first_name || "").charAt(0).toUpperCase() === tok)));
  const myInit = (pilot.first_name || "").charAt(0).toUpperCase();
  const iAmTagged = claimed.has(myInit);
  const defer = traineeDefersToFullPilot(pilot, shared);
  return new Set(surnameLabels.filter((l) => {
    const tok = tokenOf(l);
    if (iAmTagged) return tok === myInit;
    if (defer) return false;
    return !claimed.has(tok);
  }));
}

// Resolve ONE dispatch-sheet label to the SPECIFIC pilot it names (or null).
// This is the reverse of dispatchLabelsForPilot and the ONE place any feature
// should use to answer "who is this label?" before reading that pilot's
// attributes (training-pilot status, notes, email, …). Doing it any other way
// — e.g. matching a label against only a subset of pilots, or taking the first
// surname match without checking the initial — is what keeps attributing one
// pilot's data to a same-surname pilot (Brett vs Paul Merrill, 2026-07-25).
//
// Same-surname disambiguation is delegated to dispatchLabelsForPilot (the
// blessed logic My Schedule uses) so this can never drift from it: the owner
// is whichever same-surname pilot's label set contains this exact label.
// allLabels = every pilot_label in the current sheet slice (needed to know
// which initials the office actually uses to tag this surname group).
export function resolvePilotFromLabel(label, allPilots, allLabels) {
  const surname = dispatchLabelCore(label).toUpperCase().split(/\s+/).filter(Boolean)[0];
  if (!surname) return null;
  const candidates = (allPilots || []).filter(
    (p) => (p.last_name || "").toUpperCase().trim() === surname);
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];
  return candidates.find((p) => dispatchLabelsForPilot(allLabels, p, allPilots).has(label)) || null;
}

// Travel-reservation labels: "LASTNAME" or "LASTNAME, F".
export function travelLabelIsMine(label, me, sameSurnamePilots) {
  const last = (me?.last_name || "").toUpperCase().trim();
  const initial = (me?.first_name || "").trim().charAt(0).toUpperCase();
  const lab = (label || "").toUpperCase().trim();
  if (!last || !lab) return false;
  const m = lab.match(/^(.+?),\s*([A-Z])/);
  if (m) return m[1].trim() === last && m[2] === initial;
  if (lab !== last) return false;
  return !traineeDefersToFullPilot(me, sameSurnamePilots);
}
